import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2, Save, ArrowLeft, Plus, X, ImageIcon, Upload } from 'lucide-react';
import { productsAPI, categoriesAPI, brandsAPI, uploadAPI } from '../../services/api';

const AdminProductCreate = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [newImageUrl, setNewImageUrl] = useState('');
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    description: '',
    price: '',
    sale_price: '',
    category: '',
    brand: '',
    images: [],
    stock_quantity: 100,
    is_new: true,
    is_best_seller: false,
    is_featured: false,
    in_stock: true,
    // Extended specifications
    size: '',
    color: '',
    material: '',
    weight: '',
    dimensions: '',
    bullet_points: [''],
    specifications: [{ name: '', value: '' }],
  });

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      try {
        const [categoriesRes, brandsRes] = await Promise.all([
          categoriesAPI.getAll(),
          brandsAPI.getAll()
        ]);
        
        if (isMounted) {
          setCategories(categoriesRes.data || []);
          setBrands(brandsRes.data || []);
          if (categoriesRes.data?.length > 0) {
            setFormData(prev => ({ ...prev, category: categoriesRes.data[0].name }));
          }
        }
      } catch (error) {
        if (isMounted && error.name !== 'CanceledError') {
          console.error('Error loading data:', error);
        }
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const fetchCategories = async () => {};
  const fetchBrands = async () => {};

  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const generateSKU = () => {
    return 'SH-' + Date.now().toString(36).toUpperCase();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.price || !formData.category) {
      alert('Please fill required fields: Name, Price, Category');
      return;
    }
    
    setSaving(true);
    
    try {
      const productData = {
        name: formData.name,
        slug: generateSlug(formData.name) + '-' + Date.now(),
        sku: formData.sku || generateSKU(),
        description: formData.description || formData.name,
        price: parseFloat(formData.price) || 0,
        sale_price: formData.sale_price ? parseFloat(formData.sale_price) : null,
        category: formData.category,
        brand: formData.brand || 'BOXVERRA',
        images: formData.images.length > 0 ? formData.images : [`https://placehold.co/800x800/E8E4DE/8B7355?text=${encodeURIComponent(formData.name.substring(0, 15))}`],
        stock_quantity: parseInt(formData.stock_quantity) || 100,
        is_new: formData.is_new,
        is_best_seller: formData.is_best_seller,
        is_featured: formData.is_featured,
        in_stock: formData.in_stock,
        rating: 0,
        review_count: 0,
        // Extended specifications
        size: formData.size || null,
        color: formData.color || null,
        material: formData.material || null,
        weight: formData.weight || null,
        dimensions: formData.dimensions || null,
        bullet_points: formData.bullet_points.filter(bp => bp.trim() !== ''),
        specifications: formData.specifications.filter(s => s.name.trim() !== '' && s.value.trim() !== ''),
      };
      
      await productsAPI.create(productData);
      alert('Product created successfully!');
      navigate('/admin/products');
    } catch (error) {
      console.error('Error creating product:', error);
      alert('Failed to create product: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const addImage = () => {
    if (newImageUrl.trim()) {
      setFormData({
        ...formData,
        images: [...formData.images, newImageUrl.trim()]
      });
      setNewImageUrl('');
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    
    try {
      const uploadPromises = files.map(file => uploadAPI.uploadImage(file));
      const results = await Promise.all(uploadPromises);
      
      const newUrls = results.map(res => res.data.url);
      setFormData({
        ...formData,
        images: [...formData.images, ...newUrls]
      });
    } catch (error) {
      alert('Failed to upload images: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = (index) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index)
    });
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/admin/products')}
            className="text-text-heading hover:bg-soft-sand"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-4xl font-bold text-charcoal font-heading">Create Product</h1>
            <p className="text-stone-grey font-body">Add a new product to your store</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <div className="bg-white rounded-xl p-8 border border-soft-sand shadow-soft">
            <h2 className="text-2xl font-bold text-charcoal mb-6 font-heading">Basic Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Label className="font-body font-semibold">Product Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="mt-2 font-body"
                  placeholder="e.g., Zen Garden Mini Fountain"
                />
              </div>
              
              <div>
                <Label className="font-body font-semibold">SKU</Label>
                <Input
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  className="mt-2 font-body"
                  placeholder="Auto-generated if empty"
                />
              </div>
              
              <div>
                <Label className="font-body font-semibold">Stock Quantity</Label>
                <Input
                  type="number"
                  value={formData.stock_quantity}
                  onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                  className="mt-2 font-body"
                  placeholder="100"
                />
              </div>
              
              <div className="md:col-span-2">
                <Label className="font-body font-semibold">Description</Label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="mt-2 w-full px-4 py-3 border border-soft-sand rounded-lg font-body focus:border-accent focus:ring-accent"
                  placeholder="Product description..."
                />
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="bg-white rounded-xl p-8 border border-soft-sand shadow-soft">
            <h2 className="text-2xl font-bold text-charcoal mb-6 font-heading">Pricing</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="font-body font-semibold">Price ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                  className="mt-2 font-body"
                  placeholder="29.99"
                />
              </div>
              
              <div>
                <Label className="font-body font-semibold">Sale Price ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.sale_price}
                  onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })}
                  className="mt-2 font-body"
                  placeholder="19.99 (optional)"
                />
              </div>
            </div>
          </div>

          {/* Category & Brand */}
          <div className="bg-white rounded-xl p-8 border border-soft-sand shadow-soft">
            <h2 className="text-2xl font-bold text-charcoal mb-6 font-heading">Category & Brand</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="font-body font-semibold">Category *</Label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                  className="mt-2 w-full px-4 py-3 border border-soft-sand rounded-lg font-body bg-white"
                >
                  <option value="">Select category...</option>
                  {categories.map((cat) => (
                    <option key={cat._id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label className="font-body font-semibold">Brand</Label>
                <select
                  value={formData.brand}
                  onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                  className="mt-2 w-full px-4 py-3 border border-soft-sand rounded-lg font-body bg-white"
                >
                  <option value="">Select brand...</option>
                  {brands.map((brand) => (
                    <option key={brand._id} value={brand.name}>{brand.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="bg-white rounded-xl p-8 border border-soft-sand shadow-soft">
            <h2 className="text-2xl font-bold text-charcoal mb-6 font-heading">Images</h2>
            
            {/* Current Images */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {formData.images.map((img, index) => (
                <div key={index} className="relative group">
                  <img 
                    src={img} 
                    alt={`Product ${index + 1}`}
                    className="w-full aspect-square object-cover rounded-lg bg-soft-sand"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              
              {formData.images.length === 0 && (
                <div className="col-span-full flex items-center justify-center h-32 border-2 border-dashed border-soft-sand rounded-lg">
                  <div className="text-center text-text-muted">
                    <ImageIcon className="h-8 w-8 mx-auto mb-2" />
                    <p className="font-body">No images added (placeholder will be used)</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Upload Images */}
            <div className="mb-4">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                className="hidden"
                id="image-upload-create"
              />
              <label 
                htmlFor="image-upload-create"
                className="flex items-center justify-center gap-3 p-4 border-2 border-dashed border-accent/50 rounded-lg cursor-pointer hover:bg-accent/5 transition-colors"
              >
                {uploading ? (
                  <Loader2 className="h-5 w-5 animate-spin text-accent" />
                ) : (
                  <Upload className="h-5 w-5 text-accent" />
                )}
                <span className="font-body text-accent font-semibold">
                  {uploading ? 'Uploading...' : 'Click to upload images (JPG, PNG, WebP)'}
                </span>
              </label>
            </div>
            
            {/* Or Add URL */}
            <div className="flex gap-3">
              <Input
                value={newImageUrl}
                onChange={(e) => setNewImageUrl(e.target.value)}
                placeholder="Or enter image URL..."
                className="flex-1 font-body"
              />
              <Button type="button" onClick={addImage} variant="outline" className="border-accent text-accent hover:bg-accent hover:text-white">
                <Plus className="h-4 w-4 mr-2" />
                Add URL
              </Button>
            </div>
          </div>

          {/* Extended Specifications */}
          <div className="bg-white rounded-xl p-8 border border-soft-sand shadow-soft">
            <h2 className="text-2xl font-bold text-charcoal mb-6 font-heading">Product Specifications</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div>
                <Label className="font-body font-semibold">Size</Label>
                <Input
                  value={formData.size}
                  onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                  className="mt-2 font-body"
                  placeholder="e.g., 12 inches, Large"
                />
              </div>
              <div>
                <Label className="font-body font-semibold">Color</Label>
                <Input
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="mt-2 font-body"
                  placeholder="e.g., Natural Wood, White"
                />
              </div>
              <div>
                <Label className="font-body font-semibold">Material</Label>
                <Input
                  value={formData.material}
                  onChange={(e) => setFormData({ ...formData, material: e.target.value })}
                  className="mt-2 font-body"
                  placeholder="e.g., Organic Cotton, Bamboo"
                />
              </div>
              <div>
                <Label className="font-body font-semibold">Weight</Label>
                <Input
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                  className="mt-2 font-body"
                  placeholder="e.g., 0.5 lbs, 200g"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="font-body font-semibold">Dimensions</Label>
                <Input
                  value={formData.dimensions}
                  onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })}
                  className="mt-2 font-body"
                  placeholder="e.g., 10 x 8 x 4 inches"
                />
              </div>
            </div>

            {/* Bullet Points */}
            <div className="mb-6">
              <Label className="font-body font-semibold mb-3 block">Key Features (Bullet Points)</Label>
              {formData.bullet_points.map((point, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <Input
                    value={point}
                    onChange={(e) => {
                      const newPoints = [...formData.bullet_points];
                      newPoints[index] = e.target.value;
                      setFormData({ ...formData, bullet_points: newPoints });
                    }}
                    className="flex-1 font-body"
                    placeholder="Enter a key feature..."
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      const newPoints = formData.bullet_points.filter((_, i) => i !== index);
                      setFormData({ ...formData, bullet_points: newPoints.length ? newPoints : [''] });
                    }}
                    className="text-red-500 hover:bg-red-50"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={() => setFormData({ ...formData, bullet_points: [...formData.bullet_points, ''] })}
                className="mt-2 border-accent text-accent hover:bg-accent hover:text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Bullet Point
              </Button>
            </div>

            {/* Custom Specifications */}
            <div>
              <Label className="font-body font-semibold mb-3 block">Custom Specifications</Label>
              {formData.specifications.map((spec, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <Input
                    value={spec.name}
                    onChange={(e) => {
                      const newSpecs = [...formData.specifications];
                      newSpecs[index].name = e.target.value;
                      setFormData({ ...formData, specifications: newSpecs });
                    }}
                    className="flex-1 font-body"
                    placeholder="Spec name (e.g., Battery Life)"
                  />
                  <Input
                    value={spec.value}
                    onChange={(e) => {
                      const newSpecs = [...formData.specifications];
                      newSpecs[index].value = e.target.value;
                      setFormData({ ...formData, specifications: newSpecs });
                    }}
                    className="flex-1 font-body"
                    placeholder="Value (e.g., 8 hours)"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      const newSpecs = formData.specifications.filter((_, i) => i !== index);
                      setFormData({ ...formData, specifications: newSpecs.length ? newSpecs : [{ name: '', value: '' }] });
                    }}
                    className="text-red-500 hover:bg-red-50"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={() => setFormData({ ...formData, specifications: [...formData.specifications, { name: '', value: '' }] })}
                className="mt-2 border-accent text-accent hover:bg-accent hover:text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Specification
              </Button>
            </div>
          </div>

          {/* Status Flags */}
          <div className="bg-white rounded-xl p-8 border border-soft-sand shadow-soft">
            <h2 className="text-2xl font-bold text-charcoal mb-6 font-heading">Product Status</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.in_stock}
                  onChange={(e) => setFormData({ ...formData, in_stock: e.target.checked })}
                  className="w-5 h-5 text-accent"
                />
                <span className="font-body text-text-heading">In Stock</span>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_new}
                  onChange={(e) => setFormData({ ...formData, is_new: e.target.checked })}
                  className="w-5 h-5 text-accent"
                />
                <span className="font-body text-text-heading">New Arrival</span>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_best_seller}
                  onChange={(e) => setFormData({ ...formData, is_best_seller: e.target.checked })}
                  className="w-5 h-5 text-accent"
                />
                <span className="font-body text-text-heading">Best Seller</span>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  className="w-5 h-5 text-accent"
                />
                <span className="font-body text-text-heading">Featured</span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/products')}
              className="flex-1 py-6 text-lg font-body"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 bg-accent hover:bg-accent-hover text-white py-6 text-lg font-body"
            >
              {saving ? (
                <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Creating...</>
              ) : (
                <><Save className="h-5 w-5 mr-2" /> Create Product</>
              )}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminProductCreate;
